import React from "react";
import { createStyles, Tab as MaterialTab, Tabs as MaterialTabs, withStyles } from "@material-ui/core";
import TabScrollButton from "@material-ui/core/TabScrollButton";

import type { CoretexTheme } from "../../theme";

export interface CardTab {
  key: string | number;
  value: string;
  component: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

interface CardTabsProps {
  tabs: CardTab[];
  className?: string;
  defaultIndex?: number;
}

interface TabPanelProps {
  visible: boolean;
  children?: React.ReactNode;
}

const TabPanel = ({ visible, children }: TabPanelProps): JSX.Element => <>{visible && children}</>;

const Tabs = withStyles(
  createStyles((theme: CoretexTheme) => ({
    root: {
      minHeight: 30,
      borderBottom: `1px solid ${theme.palette.mono.mediumGray}`,
    },
    indicator: {
      backgroundColor: theme.palette.mono.primaryDark,
    },
  })),
)(MaterialTabs);

const Tab = withStyles(
  createStyles((theme: CoretexTheme) => ({
    root: {
      minHeight: 32,
      minWidth: 75,
      fontWeight: theme.typography.fontWeightBold as number,
      fontSize: 12,
      color: theme.palette.mono.secondaryDark,
      "&$selected": {
        color: theme.palette.mono.primaryDark,
        fontWeight: theme.typography.fontWeightBold as number,
      },
    },
    selected: {},
  })),
)(MaterialTab);

const ScrollButton = withStyles({
  root: {
    width: 28,
    overflow: "hidden",
    transition: "width 0.4s",
    "&.Mui-disabled": {
      width: 0,
    },
  },
})(TabScrollButton);

const CardTabs = ({ tabs, className, defaultIndex }: CardTabsProps): JSX.Element => {
  const [value, setValue] = React.useState<number>(defaultIndex ?? 0);

  const handleChange = (_event: React.ChangeEvent<unknown>, newValue: number) => {
    setValue(newValue);
  };

  React.useEffect(() => {
    setValue(defaultIndex ?? 0);
  }, [defaultIndex]);

  return (
    <>
      <Tabs
        className={className}
        value={value}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        indicatorColor="primary"
        textColor="primary"
        ScrollButtonComponent={ScrollButton}
      >
        {tabs.map((tab: CardTab) => (
          <Tab
            data-testid={`${tab.value}-tab`}
            key={tab.key}
            label={tab.value}
            disabled={tab.disabled}
            onClick={tab.onClick}
          />
        ))}
      </Tabs>
      {tabs.map(({ key, component }, index) => (
        <TabPanel key={key} visible={value === index}>
          {component}
        </TabPanel>
      ))}
    </>
  );
};

export default CardTabs;
